import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Modal } from '../../components/common/Modal';
import { DataTable, type Column } from '../../components/table/DataTable';
import type { QrScanningLogRow } from '../../data/qrScanningLog';
import { caseApiRequest } from '../../services/apiClient';
import { listQrScanningLogs, QR_LOG_SORT_FIELD_MAP } from '../../services/qrScanningLog';
import { formatAuditDateTime } from '../../utils/dateFormat';

type QrScanCheckInResponse = {
    caseRequestId: string;
    referenceNo?: string;
    checkedInUserId?: string;
    checkedInUserDisplayName?: string;
    previousCustodianUserId?: string | null;
    previousCustodianDisplayName?: string | null;
    autoCheckedOutPreviousCustodian?: boolean;
    alreadyCheckedIn?: boolean;
    message?: string;
};

type QrCheckInDetailsResponse = {
    caseRequestId?: string;
    referenceNo?: string;
    viewerRole?: string | null;
    destinationDetails?: {
        physicalFileReceiver?: string | null;
        divisionUnit?: string | null;
        designation?: string | null;
    } | null;
    physicalFileReferenceDetails?: {
        fileReferenceNumber?: string | null;
        fileCategory?: string | null;
        subFileCategory?: string | null;
    } | null;
    coverNoteDetails?: {
        fileReferenceNumber?: string | null;
        requestSubmittedAt?: string | null;
        submittedAgency?: string | null;
        submittedDivision?: string | null;
        caseSource?: string | null;
        fileCategory?: string | null;
        subFileCategory?: string | null;
        applicableAreas?: string[] | null;
        accusedOrSuspectName?: string | null;
        description?: string | null;
        urgencyLevel?: string | null;
        deadline?: string | null;
        urgencyJustification?: string | null;
        connectedAgdFileReferenceNumber?: string | null;
        documents?: string[] | null;
    } | null;
    qrCheckInDetails?: {
        qrCheckedInAt?: string | null;
        qrCheckedInByDisplayName?: string | null;
        qrCheckedInByRole?: string | null;
        qrCheckedInByDivision?: string | null;
    } | null;
};

// A hand-held scanner types the whole code in a few ms per key.
// A gap larger than this between keys is treated as a human typing (ignored).
// Raise to 150-300 when testing with a phone scanner app.
const SCANNER_MAX_KEY_GAP_MS = 300;
const SCANNER_MIN_LENGTH = 3;

export function formatQrScanEventLabel(event: QrScanningLogRow['event']): string {
    const normalized = event.trim().replace(/[_-]+/g, ' ').toLowerCase();
    if (normalized.includes('check in')) return 'Check-In';
    if (normalized.includes('check out')) return 'Check-Out';
    return event.replace(/[_-]+/g, ' ').replace(/\b\w/g, (letter) => letter.toUpperCase());
}

export function getQrScanBadgeClass(event: QrScanningLogRow['event']): string {
    return event.toLowerCase().includes('check out') || event.toLowerCase().includes('check_out') ? 'p-ret' : 'p-recv';
}

function formatDateTime(value: string): string {
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return value;
    return new Intl.DateTimeFormat('en-GB', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        hour12: true,
    }).format(date).replace(/\b(am|pm)\b/i, (meridiem) => meridiem.toUpperCase());
}

export default function QrScanningLogPage() {
    const { t } = useTranslation();
    const [rows, setRows] = useState<QrScanningLogRow[]>([]);
    const [totalCount, setTotalCount] = useState(0);
    const [totalPages, setTotalPages] = useState(1);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [page, setPage] = useState(1);
    const [pageSize, setPageSize] = useState(10);
    const [sort, setSort] = useState<{ key: string; dir: 'asc' | 'desc' }>({ key: 'eventAt', dir: 'desc' });
    const [scanQrOpen, setScanQrOpen] = useState(false);
    const [scannerReferenceNo, setScannerReferenceNo] = useState('');
    const [scanStatus, setScanStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');
    const [scanError, setScanError] = useState<string | null>(null);
    const [scanSuccessData, setScanSuccessData] = useState<QrCheckInDetailsResponse | null>(null);

    const fetchAbortRef = useRef<AbortController | null>(null);
    const filtersRef = useRef<Record<string, string>>({});

    const fetchList = useCallback((currentPage: number, size: number, filters: Record<string, string>, sortKey: string, sortDir: 'asc' | 'desc') => {
        fetchAbortRef.current?.abort();
        fetchAbortRef.current = new AbortController();
        const { signal } = fetchAbortRef.current;
        setLoading(true);
        setError(null);

        void listQrScanningLogs({
            page: currentPage - 1,
            pageSize: size,
            columnFilters: filters,
            sortBy: QR_LOG_SORT_FIELD_MAP[sortKey] ?? 'DATE_TIME',
            sortDir: sortDir === 'asc' ? 'ASC' : 'DESC',
            signal,
        })
            .then((result) => {
                if (signal.aborted) return;
                setRows(result.items);
                setTotalCount(result.total);
                setTotalPages(result.totalPages);
            })
            .catch((fetchError: unknown) => {
                if (signal.aborted) return;
                setError(fetchError instanceof Error ? fetchError.message : 'Could not load QR scanning logs.');
                setRows([]);
                setTotalCount(0);
                setTotalPages(1);
            })
            .finally(() => {
                if (!signal.aborted) setLoading(false);
            });
    }, []);

    const fetchRef = useRef(fetchList);

    useEffect(() => {
        fetchRef.current = fetchList;
    }, [fetchList]);

    useEffect(() => {
        fetchRef.current(page, pageSize, filtersRef.current, sort.key, sort.dir);
        return () => fetchAbortRef.current?.abort();
    }, [page, pageSize, sort.key, sort.dir]);

    const handleFiltersChange = useCallback((filters: Record<string, string>) => {
        filtersRef.current = filters;
        setPage(1);
        fetchRef.current(1, pageSize, filters, sort.key, sort.dir);
    }, [pageSize, sort.key, sort.dir]);

    const handleSortChange = useCallback((key: string, dir: 'asc' | 'desc') => {
        setSort({ key, dir });
        setPage(1);
    }, []);

    const showScanResultScreen = Boolean(scanSuccessData);

    const resetScanState = () => {
        setScannerReferenceNo('');
        setScanError(null);
        setScanStatus('idle');
        setScanSuccessData(null);
    };

    const openScanModal = () => {
        resetScanState();
        setScanQrOpen(true);
    };

    const closeScanModal = () => {
        setScanQrOpen(false);
        resetScanState();
    };

    const handleQrScanCheckIn = useCallback(async (referenceNo: string) => {
        const trimmedReferenceNo = referenceNo.trim();
        if (!trimmedReferenceNo) {
            setScanError('Reference number is required.');
            setScanStatus('error');
            return;
        }

        setScanStatus('loading');
        setScanError(null);

        try {
            const checkInResult = await caseApiRequest<QrScanCheckInResponse>(
                '/physical-file/qr-scan-check-in',
                {
                    method: 'POST',
                    body: { referenceNo: trimmedReferenceNo },
                },
            );

            if (!checkInResult.ok) {
                throw new Error(checkInResult.message || 'QR scan check-in failed.');
            }

            const caseRequestId = checkInResult?.data?.caseRequestId;
            if (!caseRequestId) {
                throw new Error('Case request id was not returned by the QR scan response.');
            }

            const detailsResult = await caseApiRequest<QrCheckInDetailsResponse>(
                `/physical-file/${encodeURIComponent(caseRequestId)}/qr-scan-check-in-details`,
                { method: 'GET' },
            );

            if (!detailsResult.ok) {
                throw new Error(detailsResult.message || 'QR scan details could not be loaded.');
            }

            setScanSuccessData(detailsResult.data ?? null);
            setScanStatus('success');
        } catch (error) {
            setScanSuccessData(null);
            setScanStatus('error');
            setScannerReferenceNo(''); // clear so the user can scan again
            setScanError(error instanceof Error ? error.message : 'Unable to complete QR scan.');
        }
    }, []);

    // ---- Hand-held scanner (keyboard / HID mode) listener ----
    const scanBufferRef = useRef('');
    const lastKeyTimeRef = useRef(0);
    const checkInRef = useRef(handleQrScanCheckIn);

    useEffect(() => {
        checkInRef.current = handleQrScanCheckIn;
    }, [handleQrScanCheckIn]);

    useEffect(() => {
        // Listen only while the popup is open and waiting for a scan
        if (!scanQrOpen || showScanResultScreen || scanStatus === 'loading') return;

        scanBufferRef.current = '';
        let idleTimer: number | undefined;

        const submit = () => {
            window.clearTimeout(idleTimer);
            const value = scanBufferRef.current.trim();
            scanBufferRef.current = '';
            if (value.length >= SCANNER_MIN_LENGTH) {
                setScannerReferenceNo(value);
                void checkInRef.current(value);
            } else {
                setScannerReferenceNo('');
            }
        };

        const onKeyDown = (e: KeyboardEvent) => {
            console.log('[scanner] key:', e.key); // debug, remove later

            const now = Date.now();

            // Long gap = human typing, discard (no manual entry allowed)
            // (the first char may be slow with phone apps, so keep a 1-char buffer)
            if (
                scanBufferRef.current.length !== 1 &&
                now - lastKeyTimeRef.current > SCANNER_MAX_KEY_GAP_MS
            ) {
                scanBufferRef.current = '';
            }
            lastKeyTimeRef.current = now;

            if (e.key === 'Enter') {
                e.preventDefault();
                submit();
                return;
            }

            // Printable characters only (ignore Shift, Ctrl, F-keys, etc.)
            if (e.key.length === 1 && !e.ctrlKey && !e.metaKey && !e.altKey) {
                e.preventDefault();
                scanBufferRef.current += e.key;
                setScannerReferenceNo(scanBufferRef.current); // shows live while scanning

                // If the scanner sends no Enter, submit after a short pause
                window.clearTimeout(idleTimer);
                idleTimer = window.setTimeout(() => {
                    // only auto-submit a complete-looking value; keep short buffers
                    if (scanBufferRef.current.trim().length >= SCANNER_MIN_LENGTH) submit();
                }, 700);
            }
        };

        // Some scanner apps paste the whole value instead of typing it
        const onPaste = (e: ClipboardEvent) => {
            const text = e.clipboardData?.getData('text')?.trim() ?? '';
            console.log('[scanner] paste:', text); // debug, remove later
            if (text.length >= SCANNER_MIN_LENGTH) {
                e.preventDefault();
                scanBufferRef.current = text;
                setScannerReferenceNo(text);
                submit();
            }
        };

        window.addEventListener('keydown', onKeyDown);
        window.addEventListener('paste', onPaste);
        return () => {
            window.clearTimeout(idleTimer);
            window.removeEventListener('keydown', onKeyDown);
            window.removeEventListener('paste', onPaste);
        };
    }, [scanQrOpen, showScanResultScreen, scanStatus]);

    const columns = useMemo<Column<QrScanningLogRow>[]>(() => {
        return [
            {
                key: 'fileRef',
                header: t('qrScanningLog.fileRef'),
                getValue: (r) => r.fileRef,
                filterPattern: /^[a-zA-Z0-9/._ -]*$/,
                render: (r) => <span className="rn">{r.fileRef}</span>,
            },
            {
                key: 'fileCategory',
                header: t('qrScanningLog.fileCategory'),
                getValue: (r) => r.fileCategory,
            },
            {
                key: 'subFileCategory',
                header: t('qrScanningLog.subFileCategory'),
                getValue: (r) => r.subFileCategory,
            },
            {
                key: 'event',
                header: t('qrScanningLog.event'),
                getValue: (r) => formatQrScanEventLabel(r.event),
                 render: (r) => (
                    <div style={{ display: 'grid', justifyItems: 'start', gap: 3 }}>
                        <span className={`pill ${getQrScanBadgeClass(r.event)}`}>
                            {formatQrScanEventLabel(r.event)}
                        </span>
                        {r.event === 'check-out' ? (
                            <span style={{ fontSize: 10, color: 'var(--t2)' }}>
                                {t('qrScanningLog.systemAutomated')}
                            </span>
                        ) : null}
                    </div>
                ),            },
            {
                key: 'eventAt',
                header: t('qrScanningLog.eventAt'),
                getValue: (r) => formatDateTime(r.eventAt),
                filterType: 'date-range',
                render: (r) => <span style={{ fontSize: 12 }}>{formatDateTime(r.eventAt)}</span>,
            },
           
         
        ];
    }, [t]);
 const viewerRole = scanSuccessData?.viewerRole?.trim().toUpperCase();
    const isMailRoomViewer =
        viewerRole === 'MAIL_OPERATIONS_OFFICER' || viewerRole === 'RECORD_ROOM_OFFICER';
    const isDocumentMoverViewer = viewerRole === 'DOCUMENT_MOVER';
    const isCounselViewer = viewerRole === 'COUNSEL_AND_OTHER';
    const showDestinationDetails =
        isDocumentMoverViewer || (!isMailRoomViewer && !isCounselViewer);

    return (
        <div style={{ padding: 0 }}>

            <div className="bc" style={{ marginBottom: 8 }}>
                <span>{t('qrScanningLog.breadcrumb')}</span>
            </div>

            {error ? <div className="si-err" style={{ marginBottom: 12 }}>{error}</div> : null}
            <div
                className="page-hdr"
                style={{ marginBottom: 12 }}
            >
                <div className="pg-title" style={{ marginBottom: 0 }}>
                    {t('qrScanningLog.title')}
                </div>

                <button
                    type="button"
                    onClick={openScanModal}
                    className="btn-create"
                >
                    <span>{t('qrScanningLog.proceedToScanQr')}</span>
                </button>
            </div>

            <DataTable
                title={t('qrScanningLog.tableTitle')}
                columns={columns}
                rows={rows}
                rowKey={(r) => r.id}
                defaultRowsPerPage={10}
                rowsPerPageOptions={[5, 10, 15, 20]}
                previousLabel={t('tasks.shared.previousBack')}
                nextLabel={t('tasks.shared.nextForward')}
                emptyMessage={t('common.noResultsFound')}
                loading={loading}
                onFiltersChange={handleFiltersChange}
                serverSort={{ key: sort.key, dir: sort.dir, onSortChange: handleSortChange }}
                serverPaging={{
                    totalCount,
                    totalPages,
                    page,
                    pageSize,
                    onPageChange: setPage,
                    onPageSizeChange: (size) => {
                        setPageSize(size);
                        setPage(1);
                    },
                }}
            />

            {/* ---------- Scan QR Code popup ---------- */}
      <Modal
                open={scanQrOpen}
                onClose={closeScanModal}
                title={showScanResultScreen ? undefined : t('qrScanningLog.scanQrCode')}
                boxClassName={showScanResultScreen ? 'qr-check-in-modal' : undefined}
            >
                {showScanResultScreen ? [
                    <div key="success-content" style={{ minWidth: 250, display: 'grid', gap: 15 }}>
                        <div
                            className="success-bar cn-cover-success"
                            style={{ marginTop: 8, marginBottom: 0 }}
                        >
                            <div className="success-icon">
                                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" aria-hidden>
                                    <polyline points="20 6 9 17 4 12" />
                                </svg>
                            </div>
                            <div>
                                <div className="success-title">QR Checked-In Successfully</div>

                            </div>
                        </div>

                        {isMailRoomViewer ? (
                            scanSuccessData?.coverNoteDetails ? (
                                <div style={{ display: 'grid', gap: 18 }}>
                                    <section>
                                        <div style={{ fontSize: 15, fontWeight: 700, color: '#1f2d2a', marginBottom: 12 }}>
                                            Cover Note
                                        </div>
                                        <div
                                            style={{
                                                display: 'grid',
                                                gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
                                                gap: 12,
                                            }}
                                        >
                                            <div className="qr-box">
                                                <label>File Reference Number</label>
                                                <p>{scanSuccessData.coverNoteDetails.fileReferenceNumber || 'N/A'}</p>
                                            </div>
                                            <div className="qr-box">
                                                <label>Request Submitted At</label>
                                                <p>
                                                    {scanSuccessData.coverNoteDetails.requestSubmittedAt
                                                        ? formatAuditDateTime(scanSuccessData.coverNoteDetails.requestSubmittedAt)
                                                        : 'N/A'}
                                                </p>
                                            </div>
                                            <div className="qr-box">
                                                <label>Submitted Agency</label>
                                                <p>{scanSuccessData.coverNoteDetails.submittedAgency || 'N/A'}</p>
                                            </div>
                                            <div className="qr-box">
                                                <label>File Category</label>
                                                <p >
                                                    {scanSuccessData.coverNoteDetails.fileCategory || 'N/A'}
                                                </p>
                                                <p className="sub">
                                                    Sub File Category: {scanSuccessData.coverNoteDetails.subFileCategory || 'N/A'}
                                                </p>
                                            </div>
                                        </div>
                                    </section>

                                    <section>
                                        <div className="cn-sum-title">Summary of Request Details</div>
                                        <div className="cn-sum-grid">
                                            <div className="cn-col">
                                                <div className="csf">
                                                    <label>Case Source</label>
                                                    <p>{scanSuccessData.coverNoteDetails.caseSource || 'N/A'}</p>
                                                </div>
                                                <div className="csf">
                                                    <label>Connected AGD File Reference Number</label>
                                                    <p>{scanSuccessData.coverNoteDetails.connectedAgdFileReferenceNumber || 'N/A'}</p>
                                                </div>
                                                <div className="csf">
                                                    <label>Accused/Suspect Name</label>
                                                    <p>{scanSuccessData.coverNoteDetails.accusedOrSuspectName || 'N/A'}</p>
                                                </div>
                                                <div className="csf">
                                                    <label>Applicable Area(s)</label>
                                                    <p>{scanSuccessData.coverNoteDetails.applicableAreas?.join(', ') || 'N/A'}</p>
                                                </div>
                                                <div className="csf">
                                                    <label>Description</label>
                                                    <p>{scanSuccessData.coverNoteDetails.description || 'N/A'}</p>
                                                </div>
                                            </div>
                                            <div className="cn-col">
                                                <div className="csf">
                                                    <label>Urgency Level</label>
                                                    <p>{scanSuccessData.coverNoteDetails.urgencyLevel || 'N/A'}</p>
                                                </div>
                                                <div className="csf">
                                                    <label>Deadline</label>
                                                    <p>
                                                        {scanSuccessData.coverNoteDetails.deadline
                                                            ? formatAuditDateTime(scanSuccessData.coverNoteDetails.deadline)
                                                            : 'N/A'}
                                                    </p>
                                                </div>
                                                <div className="csf">
                                                    <label>Urgency Justification</label>
                                                    <p>{scanSuccessData.coverNoteDetails.urgencyJustification || 'N/A'}</p>
                                                </div>
                                                <div className="csf">
                                                    <label>Documents Declared</label>
                                                    <div className="dtags">
                                                        {scanSuccessData.coverNoteDetails.documents?.length ? (
                                                            scanSuccessData.coverNoteDetails.documents.map((document, index) => (
                                                                <div key={`${document}-${index}`} className="dtag">
                                                                    <svg
                                                                        width="11"
                                                                        height="11"
                                                                        viewBox="0 0 24 24"
                                                                        fill="none"
                                                                        stroke="currentColor"
                                                                        strokeWidth="2.5"
                                                                        aria-hidden="true"
                                                                    >
                                                                        <polyline points="20 6 9 17 4 12" />
                                                                    </svg>
                                                                    {document}
                                                                </div>
                                                            ))
                                                        ) : (
                                                            <p>None</p>
                                                        )}
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </section>
                                </div>
                            ) : null
                        ) : (
                            <>
                                {showDestinationDetails && scanSuccessData?.destinationDetails ? (
                                    <div style={{ display: 'grid', gap: 12 }}>
                                        <div style={{ fontSize: 14, fontWeight: 700, color: '#1f2d2a' }}>Destination Details</div>
                                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>

                                            <div className="qr-box">
                                                <label>Physical File Receiver</label>
                                                <p>   {scanSuccessData.destinationDetails.physicalFileReceiver || 'N/A'}</p>
                                            </div>
                                            <div className="qr-box">
                                                <label>Division / Unit</label>
                                                <p>    {scanSuccessData.destinationDetails.divisionUnit || 'N/A'}</p>
                                            </div>

                                        </div>
                                        <div className="qr-box">
                                            <label>Designation</label>
                                            <p>
                                                {scanSuccessData.destinationDetails.designation || 'N/A'}
                                            </p>
                                        </div>
                                    </div>
                                ) : null}

                                <div style={{ display: 'grid', gap: 12 }}>
                                    <div style={{ fontSize: 14, fontWeight: 700, color: '#1f2d2a' }}>Physical File Reference Details</div>
                                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                                        <div className="qr-box">
                                            <label>File Reference Number</label>
                                            <p>   {scanSuccessData?.physicalFileReferenceDetails?.fileReferenceNumber || scanSuccessData?.referenceNo || 'N/A'}</p>
                                        </div>

                                        
                                        <div className="qr-box">
                                            <label>File Category</label>
                                            <p>      {scanSuccessData?.physicalFileReferenceDetails?.fileCategory || 'N/A'}
                                            </p>
                                            <p className="sub" >
                                                Sub File Category: {scanSuccessData?.physicalFileReferenceDetails?.subFileCategory || 'N/A'}
                                            </p>
                                        </div>
                                    </div>
                                </div>
                            </>
                        )}

                        <div style={{ display: 'grid', gap: 12 }}>
                            <div style={{ fontSize: 14, fontWeight: 700, color: '#1f2d2a' }}>QR Check-In Details</div>
                            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                                <div className="qr-box">
                                    <label>QR Checked-In at</label>
                                    <p>
                                        {scanSuccessData?.qrCheckInDetails?.qrCheckedInAt
                                            ? formatAuditDateTime(scanSuccessData.qrCheckInDetails.qrCheckedInAt)
                                            : 'N/A'}
                                    </p>
                                </div>
                                <div className="qr-box">
                                    <label>QR Checked-In by</label>
                                    <p>
                                        {scanSuccessData?.qrCheckInDetails?.qrCheckedInByDisplayName || 'N/A'}
                                    </p>
                                    <p className="sub">
                                        {scanSuccessData?.qrCheckInDetails?.qrCheckedInByRole || 'N/A'}
                                        {scanSuccessData?.qrCheckInDetails?.qrCheckedInByDivision
                                            ? ` - ${scanSuccessData.qrCheckInDetails.qrCheckedInByDivision}`
                                            : ''}
                                    </p>
                                </div>
                            </div>
                        </div>
                    </div>,
                    <div className="cn-footer">
                        <button className="mfb mfb-dk" type="button" onClick={closeScanModal}>
                            Back
                        </button>
                    </div>,
                ] : (
                    <div style={{ minWidth: 320, display: 'grid', gap: 16 }}>
                        <div className="k-label" >
                            {t('qrScanningLog.scanInstruction')}
                        </div>

                        {scanError ? (
                            <div style={{ color: '#b42318', fontWeight: 600 }}>{scanError}</div>
                        ) : null}

                        {!scanError && <div style={{ textAlign: 'center', marginTop: 16, minHeight: 28 }}>
                            {scannerReferenceNo ? (
                                <>
                                    <span style={{ fontSize: 14 }}>File Reference Number: </span>
                                    <span style={{ fontSize: 15, fontWeight: 700 }}>{scannerReferenceNo}</span>
                                </>
                            ) : (
                                <span style={{ color: 'var(--t3)' }}>
                                    {scanStatus === 'loading' ? 'Checking in…' : 'Waiting for QR scan…'}
                                </span>
                            )}
                        </div>}


                    </div>
                )}
            </Modal>


            
        </div>
    );
}
